import { getUtmParams } from "../../../tracking/getUtmParams";
import { airlineResponseSchema } from "../airlines";
import { airportResponseSchema } from "../airports";
import { validatePayload, validateResponse } from "../validation";
import { webappClient } from "../clients";
import { format } from "date-fns";
import { z } from "zod";
import { ClaimDetails } from "../../funnels/pre-eligibility/state/preEligibilityState";
import { Currency, Locale } from "../../../app-settings/localesAndCurrencies";
import { informedBeforehandToDays } from "../../utils/informedBeforehandToDays";
import { delayToMinutes } from "../../utils/delayToMinutes";
import { Brand } from "v2/modules/branding/Brand";
import { ItineraryFlights } from "../../modules/types/intake-funnel";

const brandSchema = z.object({
  contact_email: z.string(),
  name: z.string(),
  slug: z.string(),
  url: z.string().optional(),
  logo_url: z.string(),
  logo_present_in_webapp: z.boolean(),
  logo_width: z.number(),
  primary_color: z.string(),
  secondary_color: z.string(),
  is_expedia: z.boolean(),
  is_egencia: z.boolean(),
  reload_pii: z.boolean(),
});

const claimEnquiryCreatePayloadSchema = z.object({
  claim_enquiry: z.object({
    experiment: z.string().optional(),
    delay: z.string(),
    locale: z.string(),
    channel: z.string(),
    brand_slug: z.string().optional(),
    claim_type: z.string(),
    disruption_mins: z.object({
      min: z.string(),
      max: z.string(),
    }),
    itinerary_id: z.string().optional(),
    itinerary: z.object({
      flights: z.array(
        z.object({
          disrupted: z.boolean(),
          flight_number: z.string(),
          local_departure_date: z.string(),
          arrival_airport_code: z.string(),
          departure_airport_code: z.string(),
          airline_identifier: z.string().optional(),
        }),
      ),
    }),
    uuid: z.string().optional(),
    last_name: z.string().optional(),
    first_name: z.string().optional(),
    airline_reason: z.string().optional(),
    offered_rerouting: z.string().optional(),
    missed_connection: z.string().optional(),
    user_preferred_currency: z.string().optional(),
    ota_campaign_identifier: z.string().optional(),
    voluntarily_surrendered: z.boolean().nullish(),
    informed_beforehand_days: z
      .object({
        min: z.string(),
        max: z.string(),
      })
      .optional(),
  }),
});

const claimEnquiryCreatedResponseSchema = z.object({
  claim_enquiry: z.object({
    ahplus: z.boolean(),
    ahplus_self_assigned: z.nullable(z.unknown()),
    airline_reason: z.string(),
    arrival: z.string(),
    assignment_download_url: z.nullable(z.string()),
    booking_ref: z.nullable(z.string()),
    booking_source_identifier: z.nullable(z.string()),
    brand: z.nullable(brandSchema),
    brand_slug: z.nullable(z.string()),
    channel: z.string(),
    claim: z.nullable(z.unknown()),
    claim_id: z.nullable(z.unknown()),
    claim_type: z.string(),
    claim_valid: z.boolean(),
    compensation: z.string(),
    compensation_in_currencies: z.object({
      EUR: z.string(),
      USD: z.string(),
    }),
    created_at: z.string(),
    customer_referral_source: z.nullable(z.unknown()),
    delay: z.string(),
    delay_mins: z.number(),
    departure: z.string(),
    disrupted_flight_airline_code: z.string().nullable(),
    disrupted_flight_airline_name: z.string(),
    document_forwarding_email_address: z.nullable(z.unknown()),
    eligibility_check_blockers_ids: z.array(z.unknown()),
    eligibility_check_result: z.string().nullable(),
    first_name: z.nullable(z.unknown()),
    id: z.number(),
    itinerary: z.object({
      id: z.number(),
      links: z.object({
        travel_documents: z.string(),
      }),
      flights: z.array(
        z.object({
          id: z.number(),
          itinerary_id: z.number(),
          flight_number: z.string(),
          local_departure_date: z.string(),
          distance_km: z.number(),
          disrupted: z.boolean(),
          arrival_time: z.nullable(z.unknown()),
          departure_time: z.nullable(z.unknown()),
          flight_identifier: z.nullable(z.unknown()),
          departure_airport_code: z.string(),
          arrival_airport_code: z.string(),
          marketing_airline_name: z.string(),
          operating_airline_name: z.nullable(z.unknown()),
          delay_mins: z.nullable(z.unknown()),
          is_cancelled: z.nullable(z.unknown()),
          potentially_missed_connection: z.boolean(),
          duration_mins: z.number(),
          departure_scheduled: z.nullable(z.unknown()),
          departure_airport: airportResponseSchema.partial(),
          arrival_airport: airportResponseSchema.partial(),
          airline: airlineResponseSchema.partial(),
        }),
      ),
    }),
    itinerary_compensation: z.string(),
    last_name: z.nullable(z.unknown()),
    locale: z.string(),
    marketing_service_fee_percentage: z.number(),
    name_title: z.nullable(z.unknown()),
    optional_questions_id: z.nullable(z.unknown()),
    passenger_compensation: z.nullable(z.unknown()),
    processing_regulation: z.string(),
    processing_track: z.string(),
    revenue: z.number(),
    was_signed: z.nullable(z.unknown()),
    status_translation_key: z.string(),
    submitted_at: z.nullable(z.unknown()),
    trip_type: z.nullable(z.unknown()),
    user_preferred_currency: z.string(),
    uuid: z.string(),
    voluntarily_surrendered: z.nullable(z.unknown()),
    what_happened: z.nullable(z.unknown()),
    extra_expenses_enabled: z.nullable(z.unknown()),
    extra_expenses_revenue: z.number(),
    corporate_extra_expenses: z.nullable(z.unknown()),
    corporate_extra_expenses_corporation_id: z.nullable(z.unknown()),
    eligible_on_hold: z.boolean(),
    goodwill_payment_credit_card: z.nullable(z.unknown()),
    address: z.nullable(z.unknown()),
    claim_payout: z.nullable(z.unknown()),
    user: z.nullable(z.unknown()),
    claim_enquiry_funnel_details: z.nullable(z.unknown()),
    fellow_passengers: z.array(z.unknown()),
  }),
});

export async function createClaimEnquiry({
  claimDetails,
  itineraryFlights,
  locale,
  currency,
  brand,
  experiment,
}: {
  claimDetails: Readonly<ClaimDetails>;
  itineraryFlights: Readonly<ItineraryFlights>;
  locale: Readonly<Locale>;
  currency: Readonly<Currency>;
  brand: Brand | null | undefined;
  experiment?: string;
}) {
  const validatedPayload = validatePayload({
    data: {
      claim_enquiry: {
        locale,
        experiment,
        channel: claimDetails.channel,
        brand_slug: brand?.slug,
        delay: claimDetails.totalDelay,
        claim_type: claimDetails.claimType,
        disruption_mins: claimDetails.totalDelay && delayToMinutes(claimDetails.totalDelay),
        user_preferred_currency: currency,
        airline_reason: claimDetails.airlineReason,
        voluntarily_surrendered: claimDetails.voluntarilySurrendered,
        informed_beforehand_days: claimDetails.informedBeforehand
          ? informedBeforehandToDays(claimDetails.informedBeforehand)
          : undefined,
        itinerary_id: claimDetails.itineraryId,
        itinerary: {
          flights: itineraryFlights.map((itineraryFlight) => ({
            disrupted: itineraryFlight.disrupted,
            flight_number: itineraryFlight.flightNumber,
            local_departure_date:
              itineraryFlight.flightDate && format(itineraryFlight.flightDate, "dd-MM-yyyy"),
            arrival_airport_code: itineraryFlight.arrivalAirport?.iata,
            departure_airport_code: itineraryFlight.departureAirport?.iata,
            airline_identifier: itineraryFlight.airline?.uniqueIdentifier,
          })),
        },
      },
    },
    schema: claimEnquiryCreatePayloadSchema,
    tag: "Claim enquiry creation request payload",
  });

  const data = await webappClient
    .post("api/claim_enquiries", {
      headers: { "X-Utms": JSON.stringify(getUtmParams()) },
      json: validatedPayload,
    })
    .json();

  const result = validateResponse({
    data,
    schema: claimEnquiryCreatedResponseSchema,
    tag: "Claim enquiry creation API response",
  });

  return result.claim_enquiry;
}

export async function fetchClaimEnquiry(uuid: string) {
  try {
    const data = await webappClient.get(`api/claim_enquiries/${uuid}`).json();
    const result = validateResponse({
      data,
      schema: claimEnquiryCreatedResponseSchema,
      tag: `Claim enquiry API response for UUID ${uuid}`,
    });

    return result.claim_enquiry;
  } catch (e) {
    throw new Error(`Error fetching claim enquiry: ${e}`);
  }
}
